import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { Text } from 'styles';
import { Form } from 'components/form';
import { StyledSelect } from '../../../../components/styled-select/styled-select.style';
import {
  buildUtilityAccountAndPlan,
  utilityAccountSelectOptions,
  findUtilityAccountAndPlan,
} from './select-utility-account.helpers';
import { DELETE_MOVE_REQUEST } from '../../state-machine/services.api';

const getCopy = copyFor('settings.moving.selectAccount');

type Props = {
  context: MovingStateMachineContext;
  matches: (state: string) => boolean;
  next: (...args: any[]) => any;
};

export const SelectUtilityAccount = ({ matches, next, context }: Props) => {
  const [mutate] = useMutation(DELETE_MOVE_REQUEST);
  const [selectedUtilityAccountAndPlan, setSelectedUtilityAccountAndPlan] =
    useState(buildUtilityAccountAndPlan(context));

  const error = matches('selectAccount.accountLoadingError')
    ? getCopy('accountsLoadingError')
    : undefined;
  const { account } = context;
  const selectOptions = account ? utilityAccountSelectOptions(account) : [];

  const handleSelectedUtilityAccount = async id => {
    if (account) {
      const utilityAccountAndPlan = findUtilityAccountAndPlan(account, id);
      setSelectedUtilityAccountAndPlan(utilityAccountAndPlan);

      if (utilityAccountAndPlan?.utilityAccount?.pendingMoveRequest) {
        await mutate({
          variables: {
            input: {
              id: utilityAccountAndPlan.utilityAccount.pendingMoveRequest.id,
            },
          },
        });
      }
    }
  };

  const nextValues = {
    selectedPlan: selectedUtilityAccountAndPlan?.plan,
    selectedUtilityAccount: selectedUtilityAccountAndPlan?.utilityAccount,
  };

  return (
    <Form onSubmit={() => next(nextValues)}>
      <Text
        tag="h1"
        textStyle="heading800"
        margin={{ bottom: '16px' }}
        data-testid="new-movers"
      >
        {getCopy('title')}
      </Text>
      <Text>{getCopy('description')}</Text>
      <StyledSelect
        margin={{ top: '48px' }}
        label={getCopy('accountDropdown.label')}
        name={getCopy('accountDropdown.label')}
        onChange={handleSelectedUtilityAccount}
        options={selectOptions}
        value={selectedUtilityAccountAndPlan?.utilityAccount.id}
        errorText={error}
        loading={matches('selectAccount.loadAccounts')}
        withEmptyPlaceholder={true}
      />
      <Button fullWidth disabled={!selectedUtilityAccountAndPlan} type="submit">
        {getCopy('nextButton')}
      </Button>
      <Text
        textAlign="center"
        textStyle="paragraph400"
        opacity="medium"
        margin={{ top: '24px' }}
      >
        {getCopy('disclaimer')}
      </Text>
    </Form>
  );
};
